// 121 — Psicología Clínica (Fase 7, Tanda 9, ver PLAN.md). Transcripción campo por campo del PDF oficial del Anexo 1
// MSP: hojas idx 261-262 (footer `SNS-MSP/HCU-form.121/2021`). Informe de brechas en
// `docs/hce-fase7/brechas-121-057-057A-060.md`.
//
// Decisiones ante lo que el papel no resuelve: el instructivo habla de "dos consultas" pero la hoja trae una sola
// serie de bloques, así que se sigue la HOJA (una consulta por documento). El genograma es un dibujo libre en el papel:
// aquí es un texto libre y la leyenda impresa (simbología, relaciones, abreviaturas) va como referencia. Los
// instrumentos psicológicos son texto libre (no se inventan escalas). Los diagnósticos no se restringen al capítulo V.
// Sin FHIR: el Questionnaire anterior guardaba el examen mental bajo un linkId de examen físico (semánticamente errado)
// y no hay consumidor demostrable; queda como `Par[]` en el PDF firmado.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { MARCAS_PRE_DEF, OPCIONES_SI_NO, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
const casillas = (prefijo: string, etiquetas: readonly string[]): CampoSimple[] => etiquetas.map((e, i) => casilla(`${prefijo}${i + 1}`, e));

export const CAMPOS_MOTIVO_121: readonly CampoSimple[] = [area('motivo', 'Motivo de consulta')];

export const CAMPOS_DEMANDA_121: readonly CampoSimple[] = [
  area('demanda', 'Demanda actual / enfermedad actual'),
  { clave: 'embarazo', etiqueta: 'Embarazo', tipo: 'select', opciones: OPCIONES_SI_NO },
];

export const CAMPOS_ANTECEDENTES_121: readonly CampoSimple[] = [
  ...casillas('ap', ['Cardiopatía', 'Diabetes', 'Enf. C. vascular', 'Endócrino metabólico', 'Cáncer', 'Tuberculosis', 'Enf. mental', 'Enf. infecciosa', 'Mal formación', 'Otro']),
  area('apDescripcion', 'Antecedentes patológicos personales — datos clínico-quirúrgicos, obstétricos, alérgicos relevantes'),
];

export const CAMPOS_HABITOS_121: readonly CampoSimple[] = [
  ...casillas('hab', ['Alcohol', 'Drogas', 'Tabaco', 'Alimentación', 'Sueño', 'Otros']),
  area('habDescripcion', 'Hábitos — descripción'),
];

export const CAMPOS_FAMILIARES_121: readonly CampoSimple[] = [area('familiares', 'Antecedentes patológicos familiares')];

export const CAMPOS_GENOGRAMA_121: readonly CampoSimple[] = [area('genograma', 'Genograma (descripción de la estructura familiar y sus relaciones)')];

/** Leyenda impresa junto al genograma, como referencia. */
export const LEYENDA_GENOGRAMA_121 = {
  simbologia: ['Hombre', 'Mujer', 'Aborto', 'Matrimonio o unión legítima', 'Separación', 'Unión consanguínea', 'Divorcio', 'Hijo adoptado', 'Hija adoptada', 'Hijo biológico', 'Hija biológica', 'Informante'],
  relaciones: ['Muy estrecha', 'Muy estrecha, pero conflictiva', 'Conflictiva', 'Cercana', 'Quiebre', 'Distante'],
  abreviaturas: ['IAM infarto', 'HTA hipertensión arterial', 'DM diabetes', 'CA cáncer', 'EP epilepsia', 'TB tuberculosis', 'AS asma', 'TA tabaquismo', 'AL alcoholismo', 'DD drogo dependiente', 'DC discapacidad', 'MG migrante', 'EM embarazo', 'X fallecido'],
} as const;

export const CAMPOS_EVALUACION_121: readonly CampoSimple[] = [
  area('funciones', 'Evaluación de funciones básicas / procesos cognitivos superiores'),
  area('clinica', 'Evaluación clínica'),
];

export const NUM_INSTRUMENTOS_121 = 3;
export const CAMPOS_INSTRUMENTOS_121: readonly CampoSimple[] = [
  { clave: 'aplica', etiqueta: 'Instrumentos psicológicos — aplica', tipo: 'select', opciones: OPCIONES_SI_NO },
  ...Array.from({ length: NUM_INSTRUMENTOS_121 }, (_, i) => [
    { clave: `instrumento${i + 1}`, etiqueta: `Instrumento ${i + 1}` } as CampoSimple,
    area(`resultado${i + 1}`, `Resultado ${i + 1}`),
  ]).flat(),
];

export const NUM_DIAGNOSTICOS_121 = 4;

export const CAMPOS_PLAN_121: readonly CampoSimple[] = [area('plan', 'Plan de tratamiento (objetivos, metas y modalidad de tratamiento)')];

export interface Estado121 {
  readonly motivo: Valores;
  readonly demanda: Valores;
  readonly antecedentes: Valores;
  readonly habitos: Valores;
  readonly familiares: Valores;
  readonly genograma: Valores;
  readonly evaluacion: Valores;
  readonly instrumentos: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly plan: Valores;
}

export function estadoInicial121(): Estado121 {
  return {
    motivo: valoresVacios(CAMPOS_MOTIVO_121),
    demanda: valoresVacios(CAMPOS_DEMANDA_121),
    antecedentes: valoresVacios(CAMPOS_ANTECEDENTES_121),
    habitos: valoresVacios(CAMPOS_HABITOS_121),
    familiares: valoresVacios(CAMPOS_FAMILIARES_121),
    genograma: valoresVacios(CAMPOS_GENOGRAMA_121),
    evaluacion: valoresVacios(CAMPOS_EVALUACION_121),
    instrumentos: valoresVacios(CAMPOS_INSTRUMENTOS_121),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_121 }, () => diagnosticoConMarcaVacio()),
    plan: valoresVacios(CAMPOS_PLAN_121),
  };
}

export function paresDe121(e: Estado121): Par[] {
  return [
    ...paresDeCampos(CAMPOS_MOTIVO_121, e.motivo),
    ...paresDeCampos(CAMPOS_DEMANDA_121, e.demanda),
    ...paresDeCampos(CAMPOS_ANTECEDENTES_121, e.antecedentes, 'Antecedentes patológicos personales — '),
    ...paresDeCampos(CAMPOS_HABITOS_121, e.habitos, 'Hábitos — '),
    ...paresDeCampos(CAMPOS_FAMILIARES_121, e.familiares),
    ...paresDeCampos(CAMPOS_GENOGRAMA_121, e.genograma),
    ...paresDeCampos(CAMPOS_EVALUACION_121, e.evaluacion),
    ...paresDeCampos(CAMPOS_INSTRUMENTOS_121, e.instrumentos),
    ...e.diagnosticos.map((d, i) => ({
      pregunta: `Diagnóstico ${i + 1}`,
      respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
    })),
    ...paresDeCampos(CAMPOS_PLAN_121, e.plan),
  ].filter((p) => p.respuesta.trim() !== '');
}
